import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, Shield, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useTenant } from "../../contexts/TenantContext";
import { BrandButton } from "./TenantShell";
import { tapi, apiErr } from "../../lib/tenant";

export const StepNDA = ({ onAccepted }) => {
  const { sub, config } = useTenant();
  const [nda, setNda] = useState(null);
  const [checked, setChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { tapi.get("/auth/nda/text").then((r) => setNda(r.data)).catch(() => setNda({ text: "", version: "" })); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await tapi.post(`/t/${sub}/nda`, { accepted: true });
      toast.success("NDA accepted");
      await onAccepted();
    } catch (e) { toast.error(apiErr(e, "Couldn't record your acceptance")); }
    setBusy(false);
  };

  return (
    <div className="space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="tenant-step-nda">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: "var(--tenant)" }}><Shield size={20} /></span>
        <div>
          <h2 className="text-lg font-extrabold">Non-disclosure agreement</h2>
          <p className="text-sm text-neutral-500">Your documents are confidential. They are shared only with funders and accelerators you ask to be connected to, through {config.name} and Clinq.</p>
        </div>
      </div>
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-neutral-300" style={{ accentColor: "var(--tenant)" }} data-testid="tenant-nda-checkbox" />
        <span className="text-sm text-neutral-800">
          I agree to the terms of this non-disclosure agreement.{" "}
          <button type="button" onClick={(e) => { e.preventDefault(); setOpen(true); }} className="font-semibold underline" style={{ color: "var(--tenant)" }} data-testid="tenant-nda-view-link">View the full NDA</button>
          {nda?.version && <span className="ml-1 text-[11px] text-neutral-400">(v{nda.version})</span>}
        </span>
      </label>
      <div className="flex justify-end">
        <BrandButton onClick={submit} disabled={!checked || busy} data-testid="tenant-nda-continue">
          {busy ? <Loader2 size={14} className="animate-spin" /> : null} Accept &amp; continue <ArrowRight size={16} />
        </BrandButton>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden p-0" data-testid="tenant-nda-dialog">
          <DialogHeader className="border-b border-neutral-200 px-6 py-4">
            <DialogTitle className="flex items-center gap-2 text-base"><FileText size={16} /> Clinq Mutual NDA &amp; Non-circumvent Agreement {nda?.version && `· v${nda.version}`}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto whitespace-pre-line px-6 py-4 text-sm leading-6 text-neutral-700" data-testid="tenant-nda-text">
            {nda ? nda.text : <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
